/** @jsx jsx */
import React, { useEffect, useRef, useState } from 'react';
import { styled, css, jsx } from '@compiled/react';
import {
	graphql,
	usePreloadedQuery,
	useRefetchableFragment,
	useFragment,
	useQueryLoader,
	type PreloadedQuery,
} from 'react-relay';
import { token } from '@atlaskit/tokens';
import { borderRadius, gridSize } from '@atlassian/jira-common-styles/src/main.tsx';
import { NotFoundErrorPageAsync } from '@atlassian/jira-error-pages/src/async.tsx';
import fireErrorAnalytics from '@atlassian/jira-errors-handling/src/utils/fire-error-analytics.tsx';
import { DocumentTitle } from '@atlassian/jira-global-document-title/src/DocumentTitle.tsx';
import { useIntl } from '@atlassian/jira-intl';
import { useSuspenselessRefetch } from '@atlassian/jira-issue-hooks/src/services/use-suspenseless-refetch/index.tsx';
import Placeholder from '@atlassian/jira-placeholder/src/index.tsx';
import { createAri } from '@atlassian/jira-platform-ari/src/index.tsx';
import useDebouncedCallback from '@atlassian/jira-platform-use-debounce/src/utils/use-debounce-callback/index.tsx';
import {
	ContextualAnalyticsData,
	FireScreenAnalytics,
	SCREEN,
} from '@atlassian/jira-product-analytics-bridge';
import type { ui_softwareReleasesVersionDetail_VersionDetailInner_devOps$key } from '@atlassian/jira-relay/src/__generated__/ui_softwareReleasesVersionDetail_VersionDetailInner_devOps.graphql';
import type { ui_softwareReleasesVersionDetail_VersionDetailInner_jiraVersion$key } from '@atlassian/jira-relay/src/__generated__/ui_softwareReleasesVersionDetail_VersionDetailInner_jiraVersion.graphql';
import versionProgressQuery, {
	type ui_softwareReleasesVersionProgressQuery,
} from '@atlassian/jira-relay/src/__generated__/ui_softwareReleasesVersionProgressQuery.graphql';
import relatedWorkQuery, {
	type ui_softwareReleasesVersionRelatedWork_RelatedWorkQuery,
} from '@atlassian/jira-relay/src/__generated__/ui_softwareReleasesVersionRelatedWork_RelatedWorkQuery.graphql';
import versionDesignsQuery, {
	type ui_versionIssueAssociatedDesigns_IssueAssociatedDesignsQuery,
} from '@atlassian/jira-relay/src/__generated__/ui_versionIssueAssociatedDesigns_IssueAssociatedDesignsQuery.graphql';
import type { uiVersionDetailsQuery as VersionDetailsPreloadedQuery } from '@atlassian/jira-relay/src/__generated__/uiVersionDetailsQuery.graphql';
import REFETCH_QUERY, {
	type versionDetailPageRefetchQuery,
} from '@atlassian/jira-relay/src/__generated__/versionDetailPageRefetchQuery.graphql';
import { isDevopsFeatureDisabledInFedRamp } from '@atlassian/jira-software-devops-fedramp-utils/src/index.tsx';
import { ViewFeatureFlagsEmptyStateExperienceExperienceStart } from '@atlassian/jira-software-empty-state-experience-feature-flags/src/services/experience-tracker/index.tsx';
import { AddIssuesToVersion } from '@atlassian/jira-software-releases-add-issues-to-version/src/ui/async.tsx';
import {
	VersionApproversCardExperienceStart,
	useVersionApproversCardExperienceFail,
} from '@atlassian/jira-software-releases-approvers/src/services/experience-tracker/VersionApproversCardExperienceStart.tsx';
import { Approvers } from '@atlassian/jira-software-releases-approvers/src/ui/async.tsx';
import { useSidebarStore } from '@atlassian/jira-software-releases-issue-sidebar-use-sidebar-store/src/controllers/index.tsx';
import { IssueSidebar } from '@atlassian/jira-software-releases-issue-sidebar/src/ui/IssueSidebar.tsx';
import {
	LaunchCardExperienceStart,
	useLaunchCardExperienceFail,
} from '@atlassian/jira-software-releases-launch-card/src/services/experience-tracker/LaunchCardExperienceStart.tsx';
import { LaunchCard } from '@atlassian/jira-software-releases-launch-card/src/ui/async.tsx';
import { useVersionIssuesGraphQLVariables } from '@atlassian/jira-software-releases-version-detail-issue-list-query-param-controller/src/controllers/graphql-variables/index.tsx';
import { IssueListWithHeaderWithErrorBoundary } from '@atlassian/jira-software-releases-version-detail-issue-list/src/ui/index.tsx';
import { RefetchContext } from '@atlassian/jira-software-releases-version-detail-refetch-util/src/index.tsx';
import { VERSION_DETAIL_PADDING } from '@atlassian/jira-software-releases-version-detail-shared/src/common/constants.tsx';
import {
	useViewVersionRelatedWorkExperienceFail,
	VersionRelatedWorkExperienceStart,
} from '@atlassian/jira-software-releases-version-related-work/src/services/related-work-experience-tracker/VersionRelatedWorkExperienceSuccessTracker.tsx';
import { RelatedWork } from '@atlassian/jira-software-releases-version-related-work/src/ui/index.tsx';
import { RelatedWorkLoadingSkeleton } from '@atlassian/jira-software-releases-version-related-work/src/ui/loading-skeleton/RelatedWorkLoadingSkeleton.tsx';
import { AsyncVersionRichTextSection as VersionRichTextSection } from '@atlassian/jira-software-releases-version-rich-text-section/src/ui/async.tsx';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import {
	IssueAssociatedDesignsExperienceStart,
	useIssueAssociatedDesignsExperienceFail,
} from '@atlassian/jira-version-issue-associated-designs/src/services/experience-tracker/IssueAssociatedDesignsExperienceStart.tsx';
import {
	IssueAssociatedDesigns,
	DesignSectionSkeleton,
} from '@atlassian/jira-version-issue-associated-designs/src/ui/index.tsx';
import { VersionProgress } from '@atlassian/jira-version-progress/src/ui/index.tsx';
import { useIsActiveRoute, usePathParam } from '@atlassian/react-resource-router';
import { getWillShowNav4 } from '@atlassian/jira-navigation-apps-sidebar-nav4-rollout-core/src/common/utils/get-will-show-nav4/index.tsx';
import { NonCriticalErrorBoundary } from '../common/utils/non-critical-error-boundary.tsx';
import { convertTabToQueryVariables } from '../common/utils/query-variables.tsx';
import { useCurrentTab } from '../common/utils/use-current-tab/index.tsx';
import { ViewVersionDetailsPageExpectedErrorExperienceSuccessTracker } from '../services/experience-tracker/ViewVersionDetailsPageExperienceSuccessTracker.tsx';
import { useProjectContextResource } from '../services/get-project/index.tsx';
import { PerformanceTracker } from '../services/performance-tracker/PerformanceTracker.tsx';
import { AccessCheck } from './access-check/index.tsx';
import { Header } from './header/index.tsx';
import messages from './messages.tsx';
import { RightSidebar } from './right-sidebar/index.tsx';

const getVersionIdState = (
	versionId: string,
):
	| {
			state: 'INVALID';
	  }
	| {
			state: 'CORRECTED';
			correctedVersionId: string;
	  }
	| {
			state: 'VALID';
	  } => {
	const correctedVersionId = versionId.replace(/\D/g, '');
	// non-numeric version id is invalid
	if (correctedVersionId.length === 0) {
		return { state: 'INVALID' };
	}
	// numeric version id that has been pruned of non-numeric characters
	if (correctedVersionId.length !== versionId.length) {
		return { state: 'CORRECTED', correctedVersionId };
	}
	return { state: 'VALID' };
};

type Props = {
	queryReference: PreloadedQuery<VersionDetailsPreloadedQuery>;
};

type InnerProps = {
	jiraVersion: ui_softwareReleasesVersionDetail_VersionDetailInner_jiraVersion$key;
	devOps: ui_softwareReleasesVersionDetail_VersionDetailInner_devOps$key | null;
};

export const VersionDetailInner = ({ jiraVersion, devOps }: InnerProps): JSX.Element => {
	const [state] = useSidebarStore();
	const cloudId = useCloudId();
	const [designQueryRef, loadDesignQuery] =
		useQueryLoader<ui_versionIssueAssociatedDesigns_IssueAssociatedDesignsQuery>(
			versionDesignsQuery,
		);
	const [relatedWorkQueryRef, loadRelatedWorkQuery] =
		useQueryLoader<ui_softwareReleasesVersionRelatedWork_RelatedWorkQuery>(relatedWorkQuery);
	const [versionProgressQueryRef, loadVersionProgressQuery] =
		useQueryLoader<ui_softwareReleasesVersionProgressQuery>(versionProgressQuery);
	const onViewRelatedWorkExperienceFail = useViewVersionRelatedWorkExperienceFail();
	const onLaunchCardExperienceFail = useLaunchCardExperienceFail();
	const onVersionApproversCardExperienceFail = useVersionApproversCardExperienceFail();
	const onIssueAssociatedDesignsExperienceFail = useIssueAssociatedDesignsExperienceFail();

	const { currentTabId } = useCurrentTab();
	const devOpsData = useFragment(
		graphql`
			fragment ui_softwareReleasesVersionDetail_VersionDetailInner_devOps on DevOps {
				providers(id: $cloudAri, providerTypes: [DESIGN])
					@optIn(to: "DevOpsSecurityInJira")
					@skip(if: $isDevopsFeatureDisabledInFedRamp) {
					designProviders {
						__typename
					}
				}
			}
		`,
		devOps,
	);

	const [version, refetch] = useRefetchableFragment<
		versionDetailPageRefetchQuery,
		ui_softwareReleasesVersionDetail_VersionDetailInner_jiraVersion$key
	>(
		graphql`
			fragment ui_softwareReleasesVersionDetail_VersionDetailInner_jiraVersion on JiraVersion
			@refetchable(queryName: "versionDetailPageRefetchQuery") {
				id
				canAddAndRemoveIssues
				collapsedUis @optIn(to: "JiraVersionCollapsedUis")

				allIssues: issues(filter: ALL) {
					totalCount
				}

				...async_softwareReleasesLaunchCard_LaunchCard
				...header_softwareReleasesVersionDetail
				...ui_softwareReleasesAddIssuesToVersion_AddIssuesToVersion
				...ui_softwareReleasesVersionDetailIssueList_IssueListWithHeaderWithErrorBoundary_jiraVersion
				...async_softwareReleasesApprovers_Approvers
				...rightSidebar_softwareReleasesVersionDetail
				...async_softwareReleasesVersionRichTextSection_AsyncVersionRichTextSection
			}
		`,
		jiraVersion,
	);
	const designProviderLength = (devOpsData?.providers?.designProviders ?? []).length;
	// Designs and its associated issues query is expensive, so we load it lazily not as a part of initial query.
	// Likewise with related work items (which the progress section also accesses).
	useEffect(() => {
		if (designProviderLength > 0 && designQueryRef == null) {
			loadDesignQuery({
				versionAri: version.id,
				cloudAri: createAri({
					resourceOwner: 'jira',
					resourceType: 'site',
					resourceId: cloudId,
				}),
			});
			loadRelatedWorkQuery({
				versionAri: version.id,
				isDevopsFeatureDisabledInFedRamp: isDevopsFeatureDisabledInFedRamp(),
			});
			loadVersionProgressQuery({
				versionAri: version.id,
				isDevopsFeatureDisabledInFedRamp: isDevopsFeatureDisabledInFedRamp(),
			});
		}
	}, [
		cloudId,
		designProviderLength,
		designQueryRef,
		loadDesignQuery,
		loadRelatedWorkQuery,
		loadVersionProgressQuery,
		version.id,
	]);

	const { filters, sortBy } = useVersionIssuesGraphQLVariables();
	const [suspenselessRefetch, isRefetchLoading] = useSuspenselessRefetch<
		versionDetailPageRefetchQuery,
		ui_softwareReleasesVersionDetail_VersionDetailInner_jiraVersion$key
	>(REFETCH_QUERY, refetch, { fetchPolicy: 'network-only' });

	const [debouncedRefetchVersion] = useDebouncedCallback(() => {
		suspenselessRefetch({
			id: version.id,
			isDevopsFeatureDisabledInFedRamp: isDevopsFeatureDisabledInFedRamp(),
			shouldIncludeDevOpsFieldsViaAgs: !isDevopsFeatureDisabledInFedRamp(),
			...convertTabToQueryVariables(currentTabId),
			filters,
			sortBy,
		});
	}, 500);

	// From spa-routes, cannot import the original const due to cyclical imports
	const ROUTE_NAMES_SOFTWARE_VERSION_DETAIL_EMBED = 'software-version-detail-embed';
	const isEmbedRoute = useIsActiveRoute(ROUTE_NAMES_SOFTWARE_VERSION_DETAIL_EMBED);

	const isIssueClosed = state.status === 'CLOSED';
	const isIssueSectionCollapsed = (version.collapsedUis ?? []).includes('ISSUES');
	const issueListFooterRef = useRef<HTMLDivElement>(null);
	const [isIssueListFooterPinned, setIsIssueListFooterPinned] = useState(false);

	const handleScroll = (): void => {
		const issueListFooter = issueListFooterRef.current;
		if (issueListFooter) {
			const footerRect = issueListFooter.getBoundingClientRect();

			// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
			setIsIssueListFooterPinned(footerRect.top + footerRect.height >= window.innerHeight);
		}
	};

	const issueListContent = (
		<IssueListMinHeightContainer>
			<IssueListWithHeaderWithErrorBoundary jiraVersion={version} />
			{!isIssueSectionCollapsed &&
			version?.canAddAndRemoveIssues &&
			version?.allIssues?.totalCount ? (
				<NonCriticalErrorBoundary fallback="flag" eventLocation="addIssuesToVersion">
					<IssueListFooterContentWrapper
						isPinned={isIssueListFooterPinned}
						ref={issueListFooterRef}
					>
						<AddIssuesToVersion
							appearance="subtle"
							isIssueListFooterButton
							jiraVersion={version}
							showIcon
						/>
					</IssueListFooterContentWrapper>
				</NonCriticalErrorBoundary>
			) : null}
		</IssueListMinHeightContainer>
	);

	const shouldRenderDesignSection = !isDevopsFeatureDisabledInFedRamp() && designProviderLength > 0;

	return (
		<VersionDetailsWrapper isIssueClosed={isIssueClosed} onScroll={handleScroll}>
			<RefetchContext.Provider
				value={{ isLoading: isRefetchLoading, refetch: debouncedRefetchVersion }}
			>
				<LaunchCardExperienceStart />
				<ContainerWrapper>
					<div
						css={[
							getWillShowNav4() ? mainContentContainerStylesNew : mainContentContainerStylesOld,
						]}
					>
						<Header
							jiraVersion={version}
							shouldShowBreadcrumbs={!isEmbedRoute}
							shouldShowAutomationButton={!isEmbedRoute}
							shouldShowFeedbackButton={!isEmbedRoute}
							shouldShowGraphButton={!isEmbedRoute}
						/>
						<NonCriticalErrorBoundary fallback="flag" eventLocation="richTextSection">
							<VersionRichTextSection jiraVersion={version} />
						</NonCriticalErrorBoundary>
						{!isDevopsFeatureDisabledInFedRamp() && (
							<NonCriticalErrorBoundary
								fallback="flag"
								eventLocation="relatedWork"
								onError={onViewRelatedWorkExperienceFail}
							>
								{relatedWorkQueryRef == null ? (
									<RelatedWorkLoadingSkeleton />
								) : (
									<Placeholder
										name="jira-releases-version-details-related-work"
										fallback={<RelatedWorkLoadingSkeleton />}
									>
										<RelatedWork queryRef={relatedWorkQueryRef} />
									</Placeholder>
								)}
							</NonCriticalErrorBoundary>
						)}
						{shouldRenderDesignSection && (
							<NonCriticalErrorBoundary
								fallback="flag"
								eventLocation="issueAssociatedDesigns"
								onError={onIssueAssociatedDesignsExperienceFail}
							>
								<IssueAssociatedDesignsExperienceStart />
								{designQueryRef == null ? (
									<DesignSectionSkeleton />
								) : (
									<Placeholder
										name="jira-releases-version-details-designs"
										fallback={<DesignSectionSkeleton />}
									>
										<IssueAssociatedDesigns queryRef={designQueryRef} />
									</Placeholder>
								)}
							</NonCriticalErrorBoundary>
						)}
						{issueListContent}
					</div>
					<RightSidebar jiraVersion={version}>
						{isIssueClosed && (
							<>
								<NonCriticalErrorBoundary
									fallback="flag"
									eventLocation="launchCard"
									onError={onLaunchCardExperienceFail}
								>
									<SidebarCard>
										<LaunchCard jiraVersion={version} />
									</SidebarCard>
								</NonCriticalErrorBoundary>

								<NonCriticalErrorBoundary
									fallback="flag"
									eventLocation="approversCard"
									onError={onVersionApproversCardExperienceFail}
								>
									<VersionApproversCardExperienceStart />
									<SidebarCard>
										<Approvers jiraVersion={version} />
									</SidebarCard>
								</NonCriticalErrorBoundary>

								<NonCriticalErrorBoundary fallback="flag" eventLocation="progressCard">
									{versionProgressQueryRef != null && (
										<SidebarCard>
											<Placeholder
												name="jira-releases-version-details-progress-indicator"
												fallback={null}
											>
												<VersionProgress queryRef={versionProgressQueryRef} />
											</Placeholder>
										</SidebarCard>
									)}
								</NonCriticalErrorBoundary>
							</>
						)}
						<NonCriticalErrorBoundary fallback="flag" eventLocation="issueSidebar">
							<IssueSidebar />
						</NonCriticalErrorBoundary>
					</RightSidebar>
				</ContainerWrapper>
			</RefetchContext.Provider>
		</VersionDetailsWrapper>
	);
};

export const VersionDetail = ({ queryReference }: Props): JSX.Element => {
	const hasFiredErrorAnalytics = useRef(false);
	const { formatMessage } = useIntl();
	const { data: projectContext } = useProjectContextResource();

	const { jira, devOps } = usePreloadedQuery(
		graphql`
			query uiVersionDetailsQuery(
				$cloudAri: ID!
				$versionAri: ID!
				$skip: Boolean!
				$sortBy: JiraVersionIssuesSortInput
				$filters: JiraVersionIssuesFiltersInput
				$isDevopsFeatureDisabledInFedRamp: Boolean!
				$shouldIncludeDevOpsFieldsViaAgs: Boolean!
			) @preloadable {
				devOps {
					...ui_softwareReleasesVersionDetail_VersionDetailInner_devOps
				}
				jira @skip(if: $skip) @required(action: THROW) {
					version(id: $versionAri) @required(action: THROW) {
						__typename
						... on JiraVersion {
							__typename
							name
							status
							...ui_softwareReleasesVersionDetail_VersionDetailInner_jiraVersion
							...accessCheck_softwareReleasesVersionDetail
						}
					}
				}
			}
		`,
		queryReference,
	);
	const [versionId, setVersionId] = usePathParam('versionId');

	// replaces invalid non-numeric version ids from the url params.
	// the query data is still valid as the same regex is applied in the versionDetailsResource.
	// if the version id is invalid then fire off an error analytic.
	useEffect(() => {
		if (versionId != null) {
			const versionIdState = getVersionIdState(versionId);
			if (versionIdState.state === 'CORRECTED') {
				setVersionId(versionIdState.correctedVersionId, 'replace');
			}
			if (versionIdState.state === 'INVALID' && !hasFiredErrorAnalytics.current) {
				fireErrorAnalytics({
					meta: {
						id: 'jira.releases.version.detail.invalid.version.id',
						packageName: 'jiraSoftwareReleasesVersionDetail',
						teamName: 'nova',
					},
					error: Error(`Invalid version id in path param. Version id: ${String(versionId)}`),
					sendToPrivacyUnsafeSplunk: true,
				});
				hasFiredErrorAnalytics.current = true;
			}
		}
	}, [setVersionId, versionId]);

	// missing project context means invalid project key in path param
	if (projectContext == null) {
		return (
			<>
				<NotFoundErrorPageAsync />
				<ViewVersionDetailsPageExpectedErrorExperienceSuccessTracker reason="No Project found" />
			</>
		);
	}

	// missing jira value due to invalid non-digit version id in path param
	// causing relay to skip query to avoid invalid ari error in AGG
	if (!jira && versionId != null && getVersionIdState(versionId).state === 'INVALID') {
		return (
			<>
				<NotFoundErrorPageAsync />
				<ViewVersionDetailsPageExpectedErrorExperienceSuccessTracker reason="No Version found" />
			</>
		);
	}

	// unknown error
	if (!jira) {
		throw Error("Relay: Missing @required value at path 'jira' in 'uiVersionDetailsQuery'"); // mimics the Relay @required directive
	}
	const { version } = jira;

	// received query error graphql type
	// e.g. permissions error or version not found error
	if (version.__typename !== 'JiraVersion') {
		return (
			<>
				<NotFoundErrorPageAsync />
				<ViewVersionDetailsPageExpectedErrorExperienceSuccessTracker reason="No Version found" />
			</>
		);
	}
	const { simplified, isProjectAdmin, projectId, projectKey, projectName } = projectContext;

	return (
		<AccessCheck jiraVersion={version}>
			<ContextualAnalyticsData
				attributes={{
					simplified,
					isProjectAdmin,
					projectId,
					versionStatus: version.status,
				}}
				sourceType={SCREEN}
				sourceName="versionDetails"
			>
				<FireScreenAnalytics />
				<PerformanceTracker />
				<VersionRelatedWorkExperienceStart experienceId={projectKey} />
				<ViewFeatureFlagsEmptyStateExperienceExperienceStart experienceId={projectKey} />
				<VersionDetailInner jiraVersion={version} devOps={devOps ?? null} />
				<DocumentTitle
					title={formatMessage(messages.browserTitle, {
						projectName,
						versionName: version.name,
					})}
				/>
			</ContextualAnalyticsData>
		</AccessCheck>
	);
};

type ContainerProps = {
	isIssueClosed: boolean;
};

const ISSUE_HEADER_AND_FILTERS_SECTION_OFFSET = gridSize * 14.75;
const LEFT_CONTAINER_MIN_WIDTH = 20;

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ContainerWrapper = styled.div({
	display: 'flex',
	position: 'relative',
	flex: 1,
	overflowX: 'hidden',
	maxHeight: '100%',
	height: '100%',
	overflowY: 'hidden',
});

const mainContentContainerStylesOld = css({
	minWidth: `${LEFT_CONTAINER_MIN_WIDTH}px`,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	paddingLeft: `${VERSION_DETAIL_PADDING + gridSize}px`,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	paddingRight: `${VERSION_DETAIL_PADDING}px`,
	overflowX: 'hidden',
	overflowY: 'auto',
	flex: '1 1 100%',
});

const mainContentContainerStylesNew = css({
	minWidth: `${LEFT_CONTAINER_MIN_WIDTH}px`,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	paddingLeft: `${VERSION_DETAIL_PADDING}px`,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	paddingRight: `${VERSION_DETAIL_PADDING}px`,
	overflowX: 'hidden',
	overflowY: 'auto',
	flex: '1 1 100%',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const IssueListMinHeightContainer = styled.div({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	minHeight: `${50 * gridSize + ISSUE_HEADER_AND_FILTERS_SECTION_OFFSET}px`,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const VersionDetailsWrapper = styled.div<ContainerProps>({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	overflow: ({ isIssueClosed }) => (isIssueClosed ? 'auto' : 'visible'),
	maxHeight: '100%',
	height: '100%',
	display: 'flex',
	flexDirection: 'column',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const SidebarCard = styled.div({
	borderWidth: '1px',
	borderStyle: 'solid',

	borderColor: `${token('color.border')}`,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	borderRadius: `${borderRadius}px`,
});

type IssueListFooterProps = {
	isPinned: boolean;
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const IssueListFooterContentWrapper = styled.div<IssueListFooterProps>({
	// Used to "hide" issue list items on scroll when the number of items and the add issue button
	// exceeds the viewport height

	backgroundColor: token('color.icon.inverse'),
	// We want to hide the top border when the footer is not pinned so that this border does not
	// overlap with the issue item bottom border and cause UI jank
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	borderTop: ({ isPinned }) => (isPinned ? `1px solid ${token('color.border')}` : 'none'),
	bottom: 0,
	display: 'flex',
	flexDirection: 'column',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	height: `${gridSize * 9}px`,
	justifyContent: 'center',
	position: 'sticky',
	width: '100%',
});
